import { useCallback, useEffect, useRef } from 'react'
import {
  ConditionState,
  SortState,
  SourcingRouteId,
  StatusConditionState,
} from '../reducers/condition'
import { SortableField } from '../constants/orders'
import { Company } from '../actions'
import { css } from '@emotion/react'
import { colors } from 'plume-ui'
import {
  SelectedContactPersonRow,
  SelectedFirstInterviewDateRow,
  SelectedStatusRow,
} from '../containers/search-result'
import { TableCell } from './molucule/TableCell'
import { TableHeader } from './molucule/TableHeader'
import { ColumnState } from '../reducers/column'
import SimpleBar from 'simplebar-react'
import { APPBAR_HEIGHT } from '@/components/header/AppBar'
import {
  MyLabel,
  MyLabelId,
  SourcingRoute,
} from '../reducers/sourcingAnnotationCandidates'
import { MyLabelCell } from './tableCell/MyLabelCell'
import { SourcingRouteCell } from './tableCell/SourcingRouteCell'

type SourcingAnnotationCandidates = {
  sourcingRoutes: SourcingRoute[]
  myLabels: MyLabel[]
}

export type ResultTableProps = {
  companies: Company[]
  sorting: SortState
  columns: ColumnState
  candidates: SourcingAnnotationCandidates
  condition: ConditionState
  statusCondition: StatusConditionState
  onToggle: (name: SortableField) => any
  selectedCompanyStatus: SelectedStatusRow | null
  setSelectedCompanyStatus: React.Dispatch<
    React.SetStateAction<SelectedStatusRow | null>
  >
  selectedCompanyContactPerson: SelectedContactPersonRow | null
  setSelectedCompanyContactPerson: React.Dispatch<
    React.SetStateAction<SelectedContactPersonRow | null>
  >
  selectedCompanyFirstInterviewDate: SelectedFirstInterviewDateRow | null
  setSelectedCompanyFirstInterviewDate: React.Dispatch<
    React.SetStateAction<SelectedFirstInterviewDateRow | null>
  >
  onTriggerEditStatus: (v: string) => void
  selectedCompanyShortname: string | undefined
  onSelectSourcingRoute: (
    companyId: string,
    id: SourcingRouteId,
    name: string
  ) => void
  onDeselectSourcingRoute: (companyId: string) => void
  onRequestEditSourcingRouteModal: () => void
  onSelectMyLabel: (id: MyLabelId, companyId: string) => void
  onDeselectMyLabel: (id: MyLabelId, companyId: string) => void
  onRequestEditMyLabelModal: () => void
}

export const ResultTable: React.FC<ResultTableProps> = (props) => {
  const rowHeight = 80
  const minTableHeight = 400
  const scrollRef = useCalculateFixedElementPosition(props.companies)

  useEffect(() => {
    const searchCondition: { url: string; y_offset: string } | null =
      JSON.parse(sessionStorage.getItem('search_condition')!)
    const yOffset = searchCondition?.y_offset
    if (yOffset) {
      window.scrollTo(0, Number(yOffset))
    }
  }, [])

  const handleOnSelectMyLabel = (myLabelId: number, companyId: string) => {
    props.onSelectMyLabel(myLabelId, companyId)
  }

  const handleOnDeselectMyLabel = (myLabelId: number, companyId: string) => {
    props.onDeselectMyLabel(myLabelId, companyId)
  }

  const isInhouse = (company: Company) => {
    return company['companyId'].startsWith('A-')
  }

  return (
    <div
      css={css`
        #horizontal-scroll-bar {
          .simplebar-track {
            bottom: 0;
            position: fixed;
          }
          .simplebar-scrollbar::before {
            background-color: ${colors.gray90};
            transition: none;
            opacity: 0.8;
          }
        }
      `}
    >
      <SimpleBar
        id="horizontal-scroll-bar"
        scrollableNodeProps={{ ref: scrollRef }}
      >
        <table
          className="result-table"
          css={css`
            background-color: ${colors.white00};
            border-top: 1px solid #d9d9d9;
            white-space: nowrap;
            border-collapse: collapse;
            width: 100%;
            table-layout: fixed;
          `}
          ref={scrollRef}
          data-test-id="result-table"
        >
          <thead
            id="result-table-header"
            css={css`
              background-color: ${colors.gray40};
              color: ${colors.black30};
              z-index: 999;
              width: max-content;
            `}
          >
            <tr
              css={css`
                height: 48px;
              `}
            >
              {props.columns.setting.map((col, i) => (
                <TableHeader name={col} key={`${i}-${col}`} {...props} />
              ))}
            </tr>
          </thead>
          <tbody
            data-test-id="body"
            css={css`
              padding-top: 48px;
              display: block;
              min-height: ${minTableHeight}px;
            `}
          >
            {props.companies.map((row, rowIndex) => (
              <tr
                key={`${rowIndex}-${row.companyId}`}
                css={css`
                  height: ${rowHeight}px;
                  td {
                    padding: 8px 8px;
                    display: table-cell;
                    position: relative;
                    font-size: 14px;
                    line-height: 1.2;
                    border-bottom: 1px solid #d9d9d9;
                  }
                  .text-right {
                    text-align: right;
                  }
                  ${props.selectedCompanyShortname === row.companyId &&
                  `background: ${colors.cyan10};`}
                  &:hover {
                    ${props.selectedCompanyShortname !== row.companyId &&
                    'background: rgba(0, 0, 0, 0.03);'}
                  }
                  :hover span.edit-icon,
                  :hover span.panel-icon {
                    visibility: visible;
                  }
                `}
                data-test-id="row"
              >
                {props.columns.setting.map((col, colIndex) => {
                  switch (col) {
                    case 'myLabels':
                      return (
                        <MyLabelCell
                          key={`${colIndex}-${col}`}
                          position={{
                            rowIndex,
                            rowCount: props.companies.length,
                            rowHeight,
                            minTableHeight,
                          }}
                          myLabels={row.myLabels}
                          candidates={props.candidates.myLabels}
                          onSelectMyLabel={(myLabelId: number) =>
                            handleOnSelectMyLabel(myLabelId, row.companyId)
                          }
                          onDeselectMyLabel={(myLabelId: number) =>
                            handleOnDeselectMyLabel(myLabelId, row.companyId)
                          }
                          onRequestEditModal={props.onRequestEditMyLabelModal}
                          disabled={!isInhouse(row)}
                        />
                      )
                    case 'sourcingRoute':
                      return (
                        <SourcingRouteCell
                          key={`${colIndex}-${col}`}
                          position={{
                            rowIndex,
                            rowCount: props.companies.length,
                            rowHeight,
                            minTableHeight,
                          }}
                          selected={row.sourcingRoute}
                          candidates={props.candidates.sourcingRoutes}
                          onSelect={(id: SourcingRouteId, name: string) =>
                            props.onSelectSourcingRoute(row.companyId, id, name)
                          }
                          onDeselect={() =>
                            props.onDeselectSourcingRoute(row.companyId)
                          }
                          onRequestEditModal={
                            props.onRequestEditSourcingRouteModal
                          }
                          disabled={!isInhouse(row)}
                        />
                      )
                    default:
                      return (
                        <TableCell
                          data-test-id={col}
                          name={col}
                          data={row}
                          columns={props.columns}
                          condition={props.condition}
                          statusCondition={props.statusCondition}
                          selectedCompanyStatus={props.selectedCompanyStatus}
                          selectedCompanyContactPerson={
                            props.selectedCompanyContactPerson
                          }
                          selectedCompanyFirstInterviewDate={
                            props.selectedCompanyFirstInterviewDate
                          }
                          key={`${colIndex}-${col}`}
                          setSelectedCompanyStatus={
                            props.setSelectedCompanyStatus
                          }
                          setSelectedCompanyContactPerson={
                            props.setSelectedCompanyContactPerson
                          }
                          setSelectedCompanyFirstInterviewDate={
                            props.setSelectedCompanyFirstInterviewDate
                          }
                          onTriggerEditStatus={props.onTriggerEditStatus}
                          isInhouse={isInhouse(row)}
                        />
                      )
                  }
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </SimpleBar>
    </div>
  )
}

const useCalculateFixedElementPosition = (companies: Company[]) => {
  const scrollRef = useRef<HTMLTableElement | null>(null)

  const calculateScrollBarPosition =
    useCalculateHorizontalScrollBarPosition(scrollRef)
  const calculateTableHeaderPosition = useCalculateScrollTableHeader(scrollRef)

  const calculateFixedElementPosition = useCallback(() => {
    calculateTableHeaderPosition()
    calculateScrollBarPosition()
  }, [calculateScrollBarPosition, calculateTableHeaderPosition])

  useEffect(() => {
    calculateFixedElementPosition() // NOTE: 初回描画時にスクロールバーの位置を調整するため

    window.addEventListener('scroll', calculateFixedElementPosition)
    return () => {
      window.removeEventListener('scroll', calculateFixedElementPosition)
    }
  }, [calculateFixedElementPosition])

  useEffect(() => {
    calculateFixedElementPosition() // NOTE: 会社数が変わる際にスクロールバーの位置を調整するため
  }, [companies, calculateFixedElementPosition])

  return scrollRef
}

const useCalculateHorizontalScrollBarPosition = (
  scrollRef: React.MutableRefObject<HTMLTableElement | null>
) => {
  const horizontalScrollBarRef = useRef<HTMLElement | null>(null)

  const calculateScrollBarPosition = useCallback(() => {
    if (!horizontalScrollBarRef.current) {
      horizontalScrollBarRef.current = document.querySelector(
        '#horizontal-scroll-bar .simplebar-track'
      )
    }
    if (!scrollRef.current) return
    const elementRect = scrollRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const InViewPort =
      elementRect.bottom >= 0 && elementRect.bottom <= viewportHeight
    if (InViewPort && horizontalScrollBarRef.current) {
      horizontalScrollBarRef.current.style.position = 'absolute'
    } else if (!InViewPort && horizontalScrollBarRef.current) {
      horizontalScrollBarRef.current.style.position = 'fixed'
    }
  }, [scrollRef])
  return calculateScrollBarPosition
}

const useCalculateScrollTableHeader = (
  scrollRef: React.MutableRefObject<HTMLTableElement | null>
) => {
  const tableHeaderRef = useRef<HTMLElement | null>(null)
  const scrollTableHeader = useCallback(() => {
    if (!tableHeaderRef.current || !scrollRef.current) return
    tableHeaderRef.current.style.left = -scrollRef.current.scrollLeft + 'px'
  }, [scrollRef])

  const calculateTableHeaderPosition = useCallback(() => {
    if (!tableHeaderRef.current) {
      tableHeaderRef.current = document.querySelector('#result-table-header')
    }
    if (!scrollRef.current) return
    const elementRect = scrollRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const InViewPort =
      elementRect.top >= parseInt(APPBAR_HEIGHT) &&
      elementRect.top <= viewportHeight
    if (InViewPort && tableHeaderRef.current) {
      tableHeaderRef.current.style.position = 'absolute'
      tableHeaderRef.current.style.top = '0'
      tableHeaderRef.current.style.left = '0'
      scrollRef.current.removeEventListener('scroll', scrollTableHeader)
    } else if (!InViewPort && tableHeaderRef.current) {
      tableHeaderRef.current.style.position = 'fixed'
      tableHeaderRef.current.style.top = APPBAR_HEIGHT
      tableHeaderRef.current.style.left = -scrollRef.current.scrollLeft + 'px'
      scrollRef.current?.addEventListener('scroll', scrollTableHeader)
    }
  }, [scrollRef, scrollTableHeader])

  useEffect(() => {
    scrollRef.current?.addEventListener('scroll', scrollTableHeader)
    return () => {
      scrollRef.current?.removeEventListener('scroll', scrollTableHeader)
    }
  }, [scrollRef, scrollTableHeader])

  return calculateTableHeaderPosition
}
